import { Row } from "@/components/container/Container";
import { TripType, X4bActivitySection, mapActivityToReturnTrip, mapActivityToReturnSendBooking } from "../engine/ConciergeCardViewModel";
import ConciergeCard, { ConciergeCardProps } from "./ConciergeCard";
import { RoundButton } from "@/components/buttons/Buttons";
import { JSX, useState } from "react";
import HelpPanel from "@/components/help/HelpPanel";
import ContactSupportPanel from "../contact-support/ContactSupportPanel";
import { useMutation } from "@tanstack/react-query";
import { axiosInstance } from "@/services/axios.service";
import { ENDPOINTS } from "@/constants/endpoints";
import { ActivityHelper } from "@/modules/vehicle/engine/X4bActivityDto";
import { useNavigate } from "react-router-dom";
import { routeWithId } from "@/constants/routes";
import { useAuthenticationStore } from "@/store";
import { X4bUserRoleEnum } from "@/constants/roles";

export const ConciergeNeedsAttentionCard = ({ tripInfo }: ConciergeCardProps) => {
	const [isContactPanelOpen, setIsContactPanelOpen] = useState(false);
	const isManagerOrUser = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.User, X4bUserRoleEnum.Manager]);

	const onClickRebook = (event: React.MouseEvent<HTMLButtonElement>) => { };
	const onClickContact = (event: React.MouseEvent<HTMLButtonElement>) => setIsContactPanelOpen(true);

	let button = (<></>);
	if (tripInfo.needsAttentionIsRebookRide) {
		button = (
			<RoundButton
				id='concierge-rebook-ride'
				label="Rebook ride"
				onClick={(e) => {
					e.stopPropagation();
					onClickRebook(e);
				}}
				pt={{
					root: { className: 'text-primary60 self-end' },
					label: { className: 'text-label-large2 text-white' },
				}}
			/>
		)
	} else if (tripInfo.needsAttentionIsContactSupport) {
		button = (
			<RoundButton
				id='concierge-contact-support'
				label="Contact Support"
				outlined
				onClick={(e) => {
					e.stopPropagation();
					onClickContact(e);
				}}
				pt={{
					root: { className: 'text-primary60 bg-transparent self-end' },
					label: { className: 'text-primary60 text-label-large2' },
				}}
			/>
		)
	}

	return (
		<>
			<ConciergeCard
				tripInfo={tripInfo}
				button={button}
			/>
			<ContactSupportPanel
				isOpen={isContactPanelOpen}
				onClose={() => setIsContactPanelOpen(false)}
			/>
		</>
	);
};

export const ConciergeRequestingCard = ({ tripInfo }: ConciergeCardProps) => {
	const isManagerOrUser = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.User, X4bUserRoleEnum.Manager]);

	const cancelRequest = useMutation({
		mutationFn: () => {
			return axiosInstance.post(ENDPOINTS['/api/X4bActivity/CancelOrder'], { Id: tripInfo.id });
		},
	});

	return (
		<ConciergeCard
			tripInfo={tripInfo}
			button={isManagerOrUser ? (
				<RoundButton
					id='concierge-cancel-ride'
					label="Cancel Ride"
					disabled={cancelRequest.isPending}
					loading={cancelRequest.isPending}
					onClick={(e) => {
						e.stopPropagation();
						cancelRequest.mutate();
					}}
					pt={{
						root: { className: 'bg-error99 self-end' },
						label: { className: 'text-label-large2 text-error50' },
					}}
				/>
			) : (<></>)}
		/>
	);
};

export const ConciergeAwaitingCard = ({ tripInfo }: ConciergeCardProps) => {
	const isManagerOrUser = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.User, X4bUserRoleEnum.Manager]);

	const cancelRequest = useMutation({
		mutationFn: () => {
			return axiosInstance.post(ENDPOINTS['/api/X4bActivity/CancelOrder'], { Id: tripInfo.id });
		},
	});
	return (
		<ConciergeCard
			tripInfo={tripInfo}
			button={isManagerOrUser ? (
				<RoundButton
					id='concierge-cancel-ride'
					label="Cancel Ride"
					disabled={cancelRequest.isPending}
					loading={cancelRequest.isPending}
					onClick={(e) => {
						e.stopPropagation();
						cancelRequest.mutate();
					}}
					pt={{
						root: { className: 'bg-error99 self-end' },
						label: { className: 'text-label-large2 text-error50' },
					}}
				/>
			) : <></>}
		/>
	);
};

export const ConciergeCompletedCard = ({ tripInfo }: ConciergeCardProps) => {
	const navigate = useNavigate();
	const isManagerOrUser = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.User, X4bUserRoleEnum.Manager]);

	return (
		<ConciergeCard
			tripInfo={tripInfo}
			button={tripInfo.type === TripType.OneWay && tripInfo.activitySection === X4bActivitySection.Completed && isManagerOrUser ? (
				<RoundButton
					id='concierge-book-return-trip'
					label="Book return trip"
					onClick={(e) => {
						e.stopPropagation();

						// Send/delivery orders must reopen in the delivery flow, not the ride flow.
						if (ActivityHelper.isDelivery(tripInfo.act)) {
							const returnSendBooking = mapActivityToReturnSendBooking(tripInfo.act);
							navigate(routeWithId('/concierge/delivery/:id', 0), { state: { returnSendBooking } });
							return;
						}

						const returnTrip = mapActivityToReturnTrip(tripInfo.act);
						console.log('[ConciergeCards] Creating return trip:', {
							activityId: tripInfo.act.Id,
							returnTrip: returnTrip,
							returnTripKeys: Object.keys(returnTrip),
							initialDataKeys: returnTrip.initialData ? Object.keys(returnTrip.initialData) : null,
							navigatingTo: routeWithId('/concierge/rides/:id', 0)
						});

						// Store in sessionStorage as backup for cases where location.state fails
						// This also protects against property name obfuscation issues in production builds
						try {
							const serialized = JSON.stringify(returnTrip);
							sessionStorage.setItem('pendingReturnTrip', serialized);

							// Verify serialization worked correctly
							const testParse = JSON.parse(serialized);
							console.log('[ConciergeCards] Serialization test:', {
								originalKeys: Object.keys(returnTrip),
								deserializedKeys: Object.keys(testParse),
								hasInitialData: !!testParse.initialData,
								hasCountryFlag: !!testParse.countryFlag
							});
						} catch (error) {
							console.error('[ConciergeCards] Failed to store return trip in sessionStorage:', error);
						}

						navigate(routeWithId('/concierge/rides/:id', 0), { state: { returnTrip } });
					}}
					outlined
					pt={{
						root: { className: 'text-primary60 bg-transparent self-end' },
						label: { className: 'text-label-large2 text-primary60' },
					}}
				/>
			) : undefined}
		/>
	);
};

export const ConciergeScheduledCard = ({ tripInfo }: ConciergeCardProps) => {
	const onClick = (event: React.MouseEvent<HTMLButtonElement>) => { };
	const isManagerOrUser = useAuthenticationStore((state) => state.hasRoles)([X4bUserRoleEnum.User, X4bUserRoleEnum.Manager]);

	const cancelRequest = useMutation({
		mutationFn: () => {
			return axiosInstance.post(ENDPOINTS['/api/X4bActivity/CancelOrder'], { Id: tripInfo.id });
		},
	});


	return (
		<ConciergeCard
			tripInfo={tripInfo}
			button={(
				<Row className="gap-4 self-end">
					{ActivityHelper.isDriverAssigned(tripInfo.act) && (
						<RoundButton
							id='concierge-contact-driver'
							label="Contact Driver"
							onClick={(e) => {
								e.stopPropagation();
								onClick(e);
							}}
							outlined
							pt={{
								root: { className: 'text-primary60 bg-transparent' },
								label: { className: 'text-label-large2 text-primary60' },
							}}
						/>
					)}
					{isManagerOrUser &&
						<RoundButton
							id='concierge-cancel-ride'
							label="Cancel Ride"
							disabled={cancelRequest.isPending}
							loading={cancelRequest.isPending}
							onClick={(e) => {
								e.stopPropagation();
								cancelRequest.mutate();
							}}
							pt={{
								label: { className: 'text-label-large2 text-white' },
							}}
						/>
					}
				</Row>
			)}
		/>
	);
};


